import {
	Alert,
	Anchor,
	Button,
	Card,
	Divider,
	Group,
	ScrollArea,
	Select,
	SimpleGrid,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParentDetailsQuery } from "@features/parent-details/queries";
import { useParentStudentsQuery } from "@features/parent-students/queries";

export const Route = createFileRoute("/_layout/parents-evening/")({
	component: RouteComponent,
});

function RouteComponent() {
	const { data: parent } = useParentDetailsQuery();
	const { data: students = [], isLoading } = useParentStudentsQuery();
	const currentStudents = useMemo(
		() => students.filter((student) => !student.hasLeft),
		[students],
	);
	const [studentNo, setStudentNo] = useState<string | null>(null);

	useEffect(() => {
		if (
			!studentNo ||
			!currentStudents.some((student) => student.studentNo === studentNo)
		) {
			setStudentNo(currentStudents[0]?.studentNo ?? null);
		}
	}, [currentStudents, studentNo]);

	const student =
		currentStudents.find((item) => item.studentNo === studentNo) ?? null;
	const dateOfBirth = student?.dob ? new Date(student.dob) : null;
	const dateIsValid =
		dateOfBirth !== null && !Number.isNaN(dateOfBirth.getTime());
    const radius = 0

	return (
		<ScrollArea className="h-full" type="auto">
			<Stack p={{ base: "md", sm: "xl" }} maw={860} mx="auto" gap="sm">
				<Stack gap={4}>
					<Title order={1}>Parents&apos; Evening</Title>
					<Text c="dimmed">
						Use the details below when signing in to SchoolCloud.
					</Text>
				</Stack>

                <Button
                    component="a"
                    href="https://godalming.schoolcloud.co.uk/"
                >
                    Click here to open SchoolCloud Parents' Evening log in page
                </Button>

				<Alert
					color="red"
					title="This is a guide, not the SchoolCloud sign-in form"
				>
					Copy the details exactly as shown below into the
					SchoolCloud form.
				</Alert>

                <Select
                    label="Student"
                    placeholder={
                        isLoading ? "Loading students…" : "No current students found"
                    }
                    data={currentStudents.map((item) => ({
                        value: item.studentNo,
                        label: `${item.firstName} ${item.surname}`,
                    }))}
                    value={studentNo}
                    onChange={setStudentNo}
                    disabled={isLoading || currentStudents.length === 0}
                    searchable={currentStudents.length > 6}
                />

				<Card withBorder radius="md" p={{ base: "md", sm: "xl" }}>
					<Stack gap="xl">

						<FormSection title="Your Details">
							<SimpleGrid cols={{ base: 1, sm: 3 }}>
								<TextInput
									label="Title"
									value={parent?.title ?? ""}
									placeholder="Not recorded"
									readOnly
                                    radius={radius}
								/>
								<TextInput
									label="First Name"
									value={parent?.firstName ?? ""}
									readOnly
                                    radius={radius}
								/>
								<TextInput label="Surname" value={parent?.surname ?? ""} readOnly radius={radius} />
							</SimpleGrid>
							<SimpleGrid cols={{ base: 1, sm: 2 }}>
								<TextInput label="Email" value={parent?.emailAddress.toLowerCase() ?? ""} readOnly radius={radius} />
								<TextInput label="Confirm Email" value={parent?.emailAddress.toLowerCase() ?? ""} readOnly radius={radius} />
							</SimpleGrid>
						</FormSection>

						<FormSection title="Student's Details">
							<SimpleGrid cols={{ base: 1, sm: 2 }}>
								<TextInput
									label="First Name"
									value={student?.firstName ?? ""}
									readOnly
                                    radius={radius}
								/>
								<TextInput
									label="Surname"
									value={student?.surname ?? ""}
									readOnly
                                    radius={radius}
								/>
							</SimpleGrid>
							<Stack gap={4}>
								<Text size="sm" fw={500}>
									Date of Birth
								</Text>
								<Group grow align="flex-start" wrap="nowrap">
									<TextInput
										aria-label="Day of birth"
										value={dateIsValid ? String(dateOfBirth.getUTCDate()) : ""}
										readOnly
                                        radius={radius}
									/>
									<TextInput
										aria-label="Month of birth"
										value={
											dateIsValid
												? dateOfBirth.toLocaleString("en-GB", {
														month: "long",
														timeZone: "UTC",
													})
												: ""
										}
										readOnly
                                        radius={radius}
									/>
									<TextInput
										aria-label="Year of birth"
										value={
											dateIsValid ? String(dateOfBirth.getUTCFullYear()) : ""
										}
										readOnly
                                        radius={radius}
									/>
								</Group>
							</Stack>
						</FormSection>
					</Stack>
				</Card>

				<Text size="sm" c="dimmed">
					Ready to sign in?{" "}
					<Anchor
						href="https://godalming.schoolcloud.co.uk/"
						target="_blank"
						rel="noreferrer"
					>
						Click here
					</Anchor>
				</Text>
			</Stack>
		</ScrollArea>
	);
}

function FormSection({
	title,
	children,
}: {
	title: string;
	children: ReactNode;
}) {
	return (
		<Stack gap="md">
			<Stack gap={6}>
				<Title order={2} size="h3" fw={500}>
					{title}
				</Title>
				<Divider />
			</Stack>
			{children}
		</Stack>
	);
}
